import { isWalletAdapterCompatibleStandardWallet } from "@solana/wallet-adapter-base";
import { StandardWalletAdapter } from "@solana/wallet-standard-wallet-adapter-base";
import { getWallets } from "@wallet-standard/app";

import { solanaChain } from "@/lib/wallet/config";

import type { WalletAdapterCompatibleStandardWallet } from "@solana/wallet-adapter-base";
import type { Wallet } from "@wallet-standard/base";

/**
 * A Solana wallet other than Phantom, found through the Wallet Standard.
 *
 * The site is Phantom-first, but Phantom is not every holder's wallet. Inside
 * the Coinbase Wallet app (user agent `CoinbaseWalletRN`) there is no Phantom,
 * so Connect sent holders to install it: 16 sessions in two days connected
 * their EVM wallet and never reached a claim, five of them with 36 packs
 * waiting (2026-10-22). Coinbase Wallet, Solflare and Backpack all register a
 * Solana wallet through the Standard, which `StandardWalletAdapter` drives
 * through the same adapter interface the Phantom adapter implements.
 */

/* Phantom is served by its own adapter, with its own sign-in and silent
   re-attach. Glyph/Privy's embedded wallet registers too, but it is an
   account, not a wallet a holder chose to connect here. */
const SERVED_ELSEWHERE = /phantom|privy|glyph/i;

function servesMudwater(
  wallet: Wallet
): wallet is WalletAdapterCompatibleStandardWallet {
  try {
    return (
      typeof wallet.name === "string" &&
      !SERVED_ELSEWHERE.test(wallet.name) &&
      isWalletAdapterCompatibleStandardWallet(wallet) &&
      Array.isArray(wallet.chains) &&
      wallet.chains.includes(solanaChain) &&
      "solana:signMessage" in wallet.features
    );
  } catch {
    // A malformed or not-yet-ready injection must not break page rendering.
    return false;
  }
}

/** The first registered wallet that can connect, sign and sign transactions on this network. */
export function compatibleStandardWallet(
  wallets: readonly Wallet[]
): WalletAdapterCompatibleStandardWallet | null {
  return wallets.find(servesMudwater) ?? null;
}

export function subscribeStandardWallets(onChange: () => void): () => void {
  const registry = getWallets();
  const offRegister = registry.on("register", onChange);
  const offUnregister = registry.on("unregister", onChange);
  return () => {
    offRegister();
    offUnregister();
  };
}

/** Stable between calls while nothing registers, as `useSyncExternalStore` requires. */
export function currentStandardWallet(): WalletAdapterCompatibleStandardWallet | null {
  return compatibleStandardWallet(getWallets().get());
}

export function standardWalletAdapter(
  wallet: WalletAdapterCompatibleStandardWallet
): StandardWalletAdapter {
  return new StandardWalletAdapter({ wallet });
}
